/**
 * View component for /user/register
 *
 * On successful registration this component forwards the user back to referrer
 * or to the root if there is no referrer.
 *
 * NOTE: upon reaching this page, user can toggle between /user/login and
 * /user/register without changing the original referring source route.
 */
// import primary libraries
import { useLocation, Link } from 'react-router-dom';

// import user components
import UserLayout from '../components/UserLayout.jsx';
import UserRegisterSubmitEmail from '../components/UserRegisterSubmitEmail.jsx';
// commented out with the marketing slots below - restore together
// import useMarketingContent from '../../../global/utils/useMarketingContent';

const UserRegister = () => {
  const location = useLocation();
  const redirect = new URLSearchParams(location.search).get('redirect');

  // NOTE: For devs doing local testing, if you're running into CORS errors
  //       or not getting the marketing content to show up, try modifying the
  //       useMarketingContent HEAD error handling or having it load the
  //       marketing content from a local running static-marketing server
  // All marketing slots are commented out for now - see the matching note in UserLogin.jsx.
  // Removed until someone asks for it back; to restore, uncomment this hook, the import
  // above, and the slot blocks below, then put the slot names back in the array.
  // const { mobile, urls, errors, headerId, footerId, setSlotError } = useMarketingContent(
  //   'register'
  //   , ['header', 'footer', 'background']);

  return (
    <UserLayout
      title="Create an account"
      className="w-screen h-screen overflow-hidden"
    >
      <div className='sm:w-full mx-auto flex flex-col flex-nowrap h-full max-h-full justify-center md:justify-between relative'>
        {/* Marketing background commented out, not deleted. The OAuth consent and
            hand-off pages (server/global/oauth/templates) are the back half of this same
            sign-in journey, but express serves them standalone so they cannot pull
            marketing content. Using the static mesh on both keeps the journey consistent.
            To restore: uncomment this block, drop the mesh div below, and put 'background'
            back in the useMarketingContent slots array above.
        {urls.background && !mobile && !errors.background && (
          <iframe
            src={urls.background}
            title="Marketing background"
            aria-hidden="true"
            sandbox="allow-scripts allow-same-origin"
            className="hidden md:block w-full h-full absolute inset-0 -z-10 overflow-hidden border-0"
            onError={() => setSlotError('background')}
          />
        )}
        */}
        {/* Static S+ mesh - this is the background now, not a fallback. Not gated on
            !mobile: the body is bg-white (index.html), so without it the white wordmark
            renders white-on-white on phones. */}
        <div className="block w-full h-full absolute inset-0 -z-10 bg-primary-600 bg-[url('/img/splus-blue-mesh.jpg')] bg-cover bg-center bg-no-repeat"></div>

        {/* Marketing header commented out with the rest of the marketing slots - see the
            note on the hook above. Restore this block and the slot together. */}
        {/*
        <div id={headerId} className="flex flex-grow-0 flex-shrink-0 w-full">
          {urls.header && !errors.header && (
            <iframe
              src={urls.header}
              title="Marketing header"
              sandbox="allow-scripts allow-same-origin"
              className="h-full w-full max-h-full max-w-full flex-grow flex-shrink border-0"
              onError={() => setSlotError('header')}
            />
          )}
          {(errors.header || !urls.header) && (
            <div className="h-0 w-full"></div>
          )}
        </div>
        */}

        <div className="flex-1 min-h-0 flex flex-nowrap justify-center items-center">
          {/* Image Carousel - Removed for now in favor of the central controls + marketing background.
              To restore: add 'splash' to the useMarketingContent slots array above.
          {urls.splash && !errors.splash && (
            <div className="flex-1 p-3 items-center justify-center hidden md:flex max-w-[70%]">
              <iframe
                src={urls.splash}
                title="Marketing splash image"
                sandbox="allow-scripts allow-same-origin"
                className="w-full h-full border-0"
                onError={() => setSlotError('splash')}
              />
            </div>
          )}
          {(errors.splash || !urls.splash) && (
            <p className="text-center flex justify-center items-center w-full h-full">
              Access your Simulations Plus products by signing in through S+ Cloud
            </p>
          )} */}

          <div className="flex flex-col flex-nowrap items-center justify-start w-full max-w-full md:w-[520px] md:max-w-[520px] max-h-full gap-6 overflow-auto">
            {/* wordmark sits above the card, same as login */}
            <img
              className="w-[232px] max-w-[80%] h-auto block flex-shrink-0"
              src="/img/SP_wordmark-white.png"
              alt="Simulations Plus"
            />

            <div className="bg-neutral-pri border border-neutral-pri rounded-lg flex flex-col flex-nowrap flex-grow flex-shrink items-center justify-start w-full gap-2 p-10">
              <UserRegisterSubmitEmail
                wrapperClass='w-full'
                title="Create an account"
                showLogo={false}
                emailStepFooter={(
                  <p className='text-left text-sm text-neutral-sec m-0'>
                    Already have an account?
                    <Link
                      to={{
                        pathname: '/user/login'
                        , search: redirect ? `?redirect=${encodeURIComponent(redirect)}` : undefined
                        , state: location.state
                      }}
                      className='mx-2'
                    >
                      Sign in
                    </Link>
                  </p>
                )}
              />
            </div>

            {/* Terms + Privacy Policy - sit below the card on the field */}
            <div className="flex-grow-0 flex-shrink-0 flex flex-wrap gap-2 justify-center items-center text-tertiary-100">
              <a href="https://www.simulations-plus.com/terms-of-use/" target="_blank" rel="noreferrer" className="text-xs no-underline my-auto !text-tertiary-100">Terms of Use</a>
              <span aria-hidden="true" className="opacity-60 text-xs">&middot;</span>
              <a href="https://www.simulations-plus.com/privacy-notice/" target="_blank" rel="noreferrer" className="text-xs no-underline my-auto !text-tertiary-100">Privacy Policy</a>
            </div>
          </div>
        </div>

        {/* Marketing footer commented out with the rest of the marketing slots. */}
        {/*
        {!mobile && (
          <div id={footerId} className="hidden md:flex flex-shrink-0">
            {urls.footer && !errors.footer && (
              <iframe
                src={urls.footer}
                title="Marketing footer"
                sandbox="allow-scripts allow-same-origin"
                className="max-h-full max-w-full flex-grow flex-shrink border-0"
                onError={() => setSlotError('footer')}
              />
            )}
            {(errors.footer || !urls.footer) && (
              <div className="h-0 w-full"></div>
            )}
          </div>
        )}
        */}
      </div>
    </UserLayout>
  )
}

export default UserRegister
